import './ui/ui.css';
import { BoardEngine } from './logic/BoardEngine';
import { getLevel } from './data/campaign';
import type { Coord, LevelSpec, ProgressRecord, TurnResult } from './logic/types';
import { AudioManager } from './audio/AudioManager';
import { GameScene } from './render/GameScene';
import { UIManager, type UISettings } from './ui/UIManager';

const app = document.querySelector<HTMLElement>('#app')!;
const audio = new AudioManager();
const sceneHost = document.createElement('div');
sceneHost.className = 'scene-layer';
sceneHost.style.position = 'fixed';
sceneHost.style.inset = '0';
sceneHost.style.zIndex = '0';
sceneHost.style.pointerEvents = 'none';
document.body.appendChild(sceneHost);
let progress = loadProgress();
let engine: BoardEngine | null = null;
let activeLevel: LevelSpec | null = null;
let currentLevelId = Math.max(1, Math.min(50, progress.unlocked));
let transitionToken = 0;

const ui = new UIManager({
  start: () => startLevel(currentLevelId),
  map: () => showMap(),
  selectLevel: (level) => startLevel(level),
  next: () => startLevel(Math.min(50, (activeLevel?.id ?? currentLevelId) + 1)),
  retry: () => startLevel(currentLevelId),
  pause: () => pauseLevel(),
  resume: () => resumeLevel(),
  settings: () => { audio.ui('open'); ui.showSettings(); },
  credits: () => { audio.ui('open'); ui.showCredits(); },
  back: () => showMenu(),
  settingsChanged: (settings) => applySettings(settings),
  tutorialClosed: () => { if (engine?.isStable()) scene.setInputEnabled(true); },
});

const scene = new GameScene(sceneHost, {
  swap: (from, to) => attemptSwap(from, to),
  focus: () => audio.select(),
});

function boot(): void {
  applySettings(ui.currentSettings);
  sceneHost.style.display = 'none';
  showMenu();
  const bootScreen = document.querySelector('.boot-screen');
  bootScreen?.remove();
  let previous = performance.now();
  const frame = (now: number) => {
    const delta = Math.min(0.05, Math.max(0, (now - previous) / 1000));
    previous = now;
    scene.update(delta);
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  const resize = () => scene.resize(sceneHost.clientWidth || window.innerWidth, sceneHost.clientHeight || window.innerHeight);
  window.addEventListener('resize', resize, { passive: true });
  new ResizeObserver(resize).observe(sceneHost);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') resize(); });
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeLevel && !ui.isModalOpen()) pauseLevel();
  });
}

function startLevel(levelId: number): void {
  const level = getLevel(levelId);
  currentLevelId = level.id;
  activeLevel = level;
  engine = new BoardEngine(level);
  sceneHost.style.display = 'block';
  sceneHost.style.pointerEvents = 'auto';
  scene.resize(window.innerWidth, window.innerHeight);
  scene.setLevel(level, engine.getSnapshot(), ui.currentSettings.highContrast);
  scene.setPreferences(ui.currentSettings.reducedMotion, ui.currentSettings.reducedFlashes, ui.currentSettings.highContrast);
  scene.setInputEnabled(true);
  ui.showGame(level, engine.getSnapshot(), progress);
  void audio.resume();
  if (level.tutorial) {
    scene.setInputEnabled(false);
    ui.showTutorial(level.tutorial);
  }
}

function attemptSwap(from: Coord, to: Coord): void {
  if (!engine || !activeLevel || engine.movesRemaining <= 0 || !engine.isStable()) return;
  const before = engine.getSnapshot();
  const result = engine.playSwap(from, to);
  if (!result.accepted) {
    audio.invalid();
    scene.showInvalid(from, to);
    return;
  }
  audio.swap();
  scene.setInputEnabled(false);
  ui.updateHud(result.after);
  for (const event of result.events) {
    if (event.kind === 'clear') audio.match(event.amount ?? 3, event.cascade ?? 1);
    if (event.kind === 'special' && event.label !== 'Pattern formed') audio.special(event.special ?? 'nova', event.cascade ?? 1);
    if (event.kind === 'combo') audio.combo();
    if (event.kind === 'cascade') audio.cascade(event.cascade ?? 2);
    if (event.kind === 'damage') audio.blocker();
    if (event.kind === 'objective') audio.objective();
    if (event.kind === 'reshuffle') audio.ui('open');
    if (event.kind === 'hazard') audio.hazard(event.amount ?? 1);
  }
  const duration = scene.syncSnapshot(result.after, result.events, ui.currentSettings.highContrast);
  const token = ++transitionToken;
  window.setTimeout(() => {
    if (token !== transitionToken) return;
    if (result.won || result.lost) {
      finishLevel(result);
      return;
    }
    scene.setInputEnabled(true);
    ui.updateHud(engine?.getSnapshot() ?? before);
  }, Math.max(420, duration * 1000 + 90));
}

function finishLevel(result: TurnResult): void {
  if (!activeLevel || !engine) return;
  scene.setInputEnabled(false);
  const stars = result.won ? calculateStars(result) : 0;
  if (result.won) {
    progress = {
      ...progress,
      unlocked: Math.max(progress.unlocked, Math.min(50, activeLevel.id + 1)),
      stars: { ...progress.stars, [activeLevel.id]: Math.max(progress.stars[activeLevel.id] ?? 0, stars) },
      scores: { ...progress.scores, [activeLevel.id]: Math.max(progress.scores[activeLevel.id] ?? 0, result.after.score) },
      bestMoves: { ...progress.bestMoves, [activeLevel.id]: Math.max(progress.bestMoves[activeLevel.id] ?? 0, result.after.moves) },
      chapterSeen: { ...progress.chapterSeen, [activeLevel.chapter]: true },
    };
    saveProgress(progress);
    scene.vfx.win([]);
    audio.win();
  } else {
    scene.vfx.loss([]);
    audio.loss();
  }
  window.setTimeout(() => ui.showResult(Boolean(result.won), activeLevel!, result.after.score, stars, result.after.moves), 350);
}

function calculateStars(result: TurnResult): number {
  if (!activeLevel) return 1;
  const moveRatio = result.after.moves / activeLevel.moves;
  const cascadeBonus = result.after.cascade >= 3 ? 1 : 0;
  return Math.max(1, Math.min(3, 1 + (moveRatio > 0.22 ? 1 : 0) + (moveRatio > 0.52 || cascadeBonus ? 1 : 0)));
}

function pauseLevel(): void {
  if (!activeLevel || !engine || engine.movesRemaining <= 0 || !engine.isStable()) return;
  scene.setInputEnabled(false);
  audio.ui('open');
  ui.showPause(activeLevel);
}

function resumeLevel(): void {
  ui.closeModal();
  if (engine?.movesRemaining && engine.isStable()) scene.setInputEnabled(true);
  audio.ui('close');
}

function showMenu(): void {
  transitionToken += 1;
  activeLevel = null;
  engine = null;
  sceneHost.style.display = 'none';
  sceneHost.style.pointerEvents = 'none';
  scene.setInputEnabled(false);
  ui.showMenu(progress);
}

function showMap(): void {
  transitionToken += 1;
  activeLevel = null;
  engine = null;
  sceneHost.style.display = 'none';
  sceneHost.style.pointerEvents = 'none';
  scene.setInputEnabled(false);
  ui.showMap(progress, Math.min(4, Math.floor((progress.unlocked - 1) / 10)));
  audio.ui('open');
}

function applySettings(settings: UISettings): void {
  audio.setPreferences({
    master: settings.master ?? settings.masterVolume,
    music: settings.music ?? settings.musicVolume,
    effects: settings.effects ?? settings.effectsVolume,
  });
  scene.setPreferences(settings.reducedMotion, settings.reducedFlashes, settings.highContrast);
}

function loadProgress(): ProgressRecord {
  try {
    const stored = JSON.parse(localStorage.getItem('lumenwild.progress') ?? 'null') as Partial<ProgressRecord> | null;
    return {
      unlocked: Math.max(1, Math.min(50, Number(stored?.unlocked ?? 1))),
      stars: stored?.stars ?? {},
      scores: stored?.scores ?? {},
      bestMoves: stored?.bestMoves ?? {},
      chapterSeen: stored?.chapterSeen ?? {},
    };
  } catch {
    return { unlocked: 1, stars: {}, scores: {}, bestMoves: {}, chapterSeen: {} };
  }
}

function saveProgress(next: ProgressRecord): void {
  try { localStorage.setItem('lumenwild.progress', JSON.stringify(next)); } catch { /* private browsing can deny storage; the run remains playable */ }
}

boot();
